import {
  AppointmentHistoryEventFragment,
  Resource_AppointmentHistoryEventType,
  Resource_AppointmentRequestActionType,
  useGetAppointmentHistoryQuery,
} from '@/generated/graphql';
import styles from '@components/appointment-details/appointment-details.module.scss';
import {AveliosButton} from '@components/common-layout/avelios-button';
import {Card, Space, Spin, Timeline} from 'antd';
import Title from 'antd/lib/typography/Title';
import dayjs from 'dayjs';
import {FC, ReactNode, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {buildTimestampDateTime} from '../../helpers/formatters';

interface AppointmentHistoryProps {
  clinicId: string;
  appointmentId: string;
  isDirectBooking: boolean;
}

const MAX_TIMELINE_ELEMENTS = 5;

const AppointmentHistoryEvent: FC<{ event: AppointmentHistoryEventFragment }> = ({ event }) => {
  const { formatMessage } = useIntl();

  const eventType = event.eventType;
  const actionType = event.actionType;

  let historyEventText: ReactNode | null;

  switch (eventType) {
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeBooked:
      const startTimeUnix = event.startTime?.seconds;

      switch (actionType) {
        case Resource_AppointmentRequestActionType.AppointmentRequestActionTypeBook:
          historyEventText = (
            <Space>
              <span>{formatMessage({ id: 'appointment.history.event.booked.booked' })}</span>
              {startTimeUnix && (
                <span>({dayjs.unix(startTimeUnix).format('DD.MM.YYYY HH:mm')})</span>
              )}
            </Space>
          );
          break;
        case Resource_AppointmentRequestActionType.AppointmentRequestActionTypeUpdate:
          historyEventText = (
            <Space>
              <span>{formatMessage({ id: 'appointment.history.event.booked.update' })}</span>
              {startTimeUnix && (
                <span>({dayjs.unix(startTimeUnix).format('DD.MM.YYYY HH:mm')})</span>
              )}
            </Space>
          );
          break;
        default:
          console.error(`Action type ${actionType} not implemented`);
          historyEventText = null;
      }
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeCancelled:
      historyEventText = formatMessage({ id: 'appointment.history.event.cancelled' });
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeCounterRequestSubmitted:
      historyEventText = formatMessage({
        id: 'appointment.history.event.counter-request-submitted',
      });
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeFinished:
      historyEventText = formatMessage({
        id: 'appointment.history.event.finished',
      });
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeMissed:
      historyEventText = formatMessage({ id: 'appointment.history.event.missed' });
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeOngoing:
      historyEventText = formatMessage({ id: 'appointment.history.event.ongoing' });
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeProposalsRejected:
      switch (actionType) {
        case Resource_AppointmentRequestActionType.AppointmentRequestActionTypeBook:
          historyEventText = formatMessage({
            id: 'appointment.history.event.proposals-rejected.booked',
          });
          break;
        case Resource_AppointmentRequestActionType.AppointmentRequestActionTypeUpdate:
          historyEventText = formatMessage({
            id: 'appointment.history.event.proposals-rejected.update',
          });
          break;
        default:
          console.error(`Event type ${eventType} not defined for action type ${actionType}`);
          historyEventText = null;
      }
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeProposalAccepted:
      historyEventText = formatMessage({ id: 'appointment.history.event.proposals-accepted' });
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeProposalReceived:
      historyEventText = formatMessage({ id: 'appointment.history.event.proposals-received' });
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeRequested:
      switch (actionType) {
        case Resource_AppointmentRequestActionType.AppointmentRequestActionTypeBook:
          historyEventText = formatMessage({ id: 'appointment.history.event.requested.book' });
          break;
        case Resource_AppointmentRequestActionType.AppointmentRequestActionTypeUpdate:
          historyEventText = formatMessage({ id: 'appointment.history.event.requested.update' });
          break;
        default:
          console.error(`Event type ${eventType} not defined for action type ${actionType}`);
          historyEventText = null;
      }
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeRequestApproved:
      historyEventText = formatMessage({ id: 'appointment.history.event.request-approved' });
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeRequestRejected:
      historyEventText = formatMessage({ id: 'appointment.history.event.request-rejected' });
      break;
    case Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeReserved:
      historyEventText = formatMessage({ id: 'appointment.history.event.reserved' });
      break;
    default:
      console.error(`Event type ${eventType} not defined`);
      historyEventText = null;
  }

  return <>{historyEventText}</>;
};

export const AppointmentHistory: FC<AppointmentHistoryProps> = ({
  appointmentId,
  clinicId,
  isDirectBooking
}) => {
  const { formatMessage } = useIntl();

  const [showAll, setShowAll] = useState(false);

  const { data: appointmentHistoryData, loading: appointmentHistoryLoading } =
    useGetAppointmentHistoryQuery({
      variables: {
        clinicId,
        appointmentId,
      },
    });

  const appointmentEvents = appointmentHistoryData?.getAppointmentHistory?.response?.events;

  const filteredEvents = useMemo(() => {
    const events = appointmentEvents?.filterPresent() ?? [];
    const reversed = [...events].reverse();

    if (isDirectBooking) {
      return reversed.filter(
        (event) =>
          event.eventType !==
          Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeRequested &&
          event.eventType !==
          Resource_AppointmentHistoryEventType.AppointmentHistoryEventTypeRequestApproved,
      );
    }

    return reversed;
  }, [appointmentEvents, isDirectBooking]);

  const historyEvents = useMemo(() => {
    return showAll ? filteredEvents : filteredEvents.slice(0, MAX_TIMELINE_ELEMENTS);
  }, [filteredEvents, showAll]);

  return (
    <Card>
      <Spin spinning={appointmentHistoryLoading}>
        <Title level={3} style={{ fontWeight: 'bold', marginBottom: 10 }}>
          {formatMessage({ id: 'appointment.history' })}
        </Title>
        <Timeline>
          {historyEvents.map((event, index) => (
            <Timeline.Item key={index} color={'darkorange'}>
              <div>
                <div className={styles.rebookingEvent}>
                  {buildTimestampDateTime(event.timestamp)}
                </div>
                <AppointmentHistoryEvent event={event} />
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
        {!showAll && filteredEvents.length > 5 && (
          <AveliosButton block onClick={() => setShowAll(true)} type={'default'}>
            {formatMessage({ id: 'appointment.history.more' })}
          </AveliosButton>
        )}
      </Spin>
    </Card>
  );
};
